import clsx from "clsx";
import { Namespace, TFunction } from "i18next";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import { SubmitButton } from "@/components/Interface/Button/Button";
import { Modal } from "@/components/Interface/Modal/Modal";
import { LocalLoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { useAlert } from "@/hooks/useAlert";
import { useCustomerLogin } from "@/hooks/useCustomerLogin";
import { DataContext } from "@/provider/DataContextProvider";
import { useStartEmailVerification, useValidateOTP } from "@/services/api";
import { AlertTypes } from "@/utils/atoms";
import { LOGIN_ERROR_PATTERNS } from "@/utils/loginErrorHandler";

import {
  TwoFactorConfirmationCodeForm,
  TwoFactorConfirmationInput,
} from "./AuthenticatorSetup/TwoFactorConfirmationInput";

interface Props {
  email: string;
  password: string;
  handleClose: () => void;
  handleSuccess: () => void;
}

const getEmailVerificationError = (
  t: TFunction<Namespace, undefined, Namespace>,
  message: string,
) => {
  if (typeof message === "string") {
    if (message.includes(LOGIN_ERROR_PATTERNS.INVALID_VERIFICATION_CODE)) {
      return t("portal:authenticator.verifyEmail.error.invalidCode");
    }
    if (message.includes(LOGIN_ERROR_PATTERNS.VERIFICATION_LOCKED)) {
      return t("portal:authenticator.verifyEmail.error.maxRetry");
    }
    if (message.includes(LOGIN_ERROR_PATTERNS.VERIFICATION_CODE_LOCKED)) {
      return t("portal:authenticator.verifyEmail.error.maxCodes");
    }
  }

  return t("translation:Common.generalForm.error.genericError");
};

export const VerifyEmailModal: FC<Props> = ({
  email,
  password,
  handleClose,
  handleSuccess,
}) => {
  const { t } = useTranslation();
  const { loginAsCustomer } = useCustomerLogin();
  const showAlert = useAlert();
  const { setDataContext } = useContext(DataContext);
  const methods = useForm<TwoFactorConfirmationCodeForm>({
    defaultValues: { code: "" },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [error, setError] = useState<string>();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCountdown = () => {
    // Clear any existing interval before starting a new one
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setCountdown(60);

    const id = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    intervalRef.current = id;
  };

  useEffect(() => {
    startCountdown();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mutateAsync: startVerification } = useStartEmailVerification();
  const { mutateAsync: validateOTP } = useValidateOTP();

  const resendCode = async () => {
    setIsLoading(true);

    // To be able to send the resend request the user needs to be already logged in...
    loginAsCustomer({
      email,
      password,
      skipEmailVerificationCheck: true,
    })
      .then(async () => {
        // To be safe the user is properly logged in we need to utilize timeout
        await new Promise((resolve) =>
          setTimeout(() => resolve(undefined), 2000),
        ).then(() =>
          startVerification()
            .then(() => {
              showAlert({
                type: AlertTypes.success,
                text: t("portal:authenticator.verifyEmail.successResend"),
              });
            })
            .catch((e) =>
              setError(getEmailVerificationError(t, e?.response?.data)),
            ),
        );
      })
      .finally(() => {
        setIsLoading(false);
        startCountdown();
      });
  };

  return (
    <FormProvider {...methods}>
      <Modal
        title={
          <p className="text-xl font-semibold text-secondary-100">
            {t("portal:authenticator.verifyEmail.confirmEmailAddress")}
          </p>
        }
        onClose={() => {}}
        showCloseButton={false}
        hasScroll={false}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();

            methods.handleSubmit(async (data) => {
              setIsLoading(true);

              // To be able to send the validate request the user needs to be already logged in...
              try {
                // Log in user (needed for OTP endpoint)
                await loginAsCustomer({
                  email,
                  password,
                  skipEmailVerificationCheck: true,
                });

                // Small delay to ensure login state is propagated
                await new Promise((resolve) => setTimeout(resolve, 2000));

                // Validate OTP
                await validateOTP({
                  params: {
                    code: Number(data.code), // Safe cast, there won't be any leading zeros according to Compax
                  },
                });

                setDataContext((prev) => ({
                  ...prev,
                  isEmailVerified: true,
                }));

                showAlert({
                  type: AlertTypes.success,
                  text: t("portal:authenticator.verifyEmail.success"),
                });

                handleSuccess();
              } catch (e: any) {
                // On any failure, reset the code and show an error
                methods.reset({ code: "" });

                setError(getEmailVerificationError(t, e?.response?.data));
              } finally {
                setIsLoading(false);
              }
            })(e);
          }}
        >
          <div className="text-black">
            <Trans
              i18nKey="portal:authenticator.verifyEmail.description"
              values={{
                email,
              }}
            />
            <div className="mt-6">
              <TwoFactorConfirmationInput
                error={error}
                clearError={() => setError(undefined)}
              />
            </div>
            <button
              className="mt-2 w-fit font-semibold text-highlight-100 underline disabled:opacity-50"
              type="button"
              onClick={async () => await resendCode()}
              disabled={countdown > 0 || isLoading}
            >
              {t("portal:authenticator.sms.verification.resend")}
              {countdown > 0 &&
                ` ${t("portal:authenticator.sms.verification.secondsLeft", { count: countdown })}`}
            </button>
            <div className="mt-6">
              <span>
                {t("portal:authenticator.verifyEmail.wrongMailAddress")}
              </span>
              <button
                className="ml-4 font-semibold text-highlight-100 underline"
                type="button"
                onClick={() => {
                  handleClose();
                }}
                disabled={isLoading}
              >
                {t("portal:authenticator.verifyEmail.enterEmailAgain")}
              </button>
            </div>

            <SubmitButton
              key={`${isLoading}`}
              className="accent relative ml-auto mt-10"
              label={
                <div className="relative flex w-full items-center justify-center">
                  {/* To not change width text and spinner are kept in button at same time */}
                  <span
                    className={clsx(
                      "absolute inset-0 flex items-center justify-center transition-opacity duration-200",
                      isLoading ? "opacity-100" : "opacity-0",
                    )}
                  >
                    <LocalLoadingSpinner className="!h-6 !w-6" />
                  </span>

                  <span
                    className={clsx(
                      "transition-opacity duration-200",
                      isLoading ? "opacity-0" : "opacity-100",
                    )}
                  >
                    {t("portal:authenticator.verifyEmail.confirmCode")}
                  </span>
                </div>
              }
              disabled={isLoading}
            />
          </div>
        </form>
      </Modal>
    </FormProvider>
  );
};
